import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import messages from './ProfilePage.messages';
import { ReactComponent as RadioIcon } from '../../src/profile/assets/radio-button.svg';
import { ReactComponent as RadioEmptyIcon } from '../../src/profile/assets/radio-button-empty.svg';
import CustomSelect from './components/CustomSelect/CustomSelect';

const EditableField = ({
  value,
  field,
  saveData,
  label,
  onDataChange,
  onSaveComplete,
  changeHandler,
  initialData,
}) => {
  const { formatMessage } = useIntl();
  const [selectedGender, setSelectedGender] = useState(initialData || '');

  useEffect(() => {
    setSelectedGender(initialData || '');
  }, [initialData]);

  useEffect(() => {
    if (saveData) {
      onSaveComplete();
    }
  }, [saveData, onSaveComplete]);

  const handleChange = newValue => {
    setSelectedGender(newValue);

    if (changeHandler) {
      changeHandler(field, newValue);
    }

    if (onDataChange) {
      onDataChange(newValue);
    }
  };

  if (!value) {
    return null;
  }

  const genderOptions = [
    {
      value: '',
      label: '——',
      displayLabel: formatMessage(messages.selectOptionDefault),
    },
    {
      value: 'm',
      label: formatMessage(messages.selectOptionMale),
      displayLabel: formatMessage(messages.selectOptionMale),
    },
    {
      value: 'f',
      label: formatMessage(messages.selectOptionFemale),
      displayLabel: formatMessage(messages.selectOptionFemale),
    },
    {
      value: 'o',
      label: formatMessage(messages.selectOptionOther),
      displayLabel: formatMessage(messages.selectOptionOther),
    },
  ];

  const renderOption = (option, currentValue) => {
    const isSelected = option.value === currentValue;
    const Icon = isSelected ? RadioIcon : RadioEmptyIcon;

    return (
      <>
        <Icon className="tw-w-4 tw-h-4 tw-min-w-4 tw-min-h-4" />
        <span className={option.value === '' ? 'tw-text-neutral-500' : ''}>{option.label}</span>
      </>
    );
  };

  return (
    <div>
      <div className="tw-flex tw-flex-col tw-mb-[15px]">
        <p className="!tw-mb-2 tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[100%]">
          {label}
        </p>
        <p className="tw-text-[14px] tw-text-placeholder tw-font-medium !tw-mb-[14px] tw-leading-[120%]">
          {formatMessage(messages['profile.user.gender.placeholder'])}
        </p>
        <CustomSelect
          options={genderOptions}
          value={selectedGender}
          onChange={handleChange}
          placeholder={formatMessage(messages.selectOptionDefault)}
          renderOption={renderOption}
          getOptionLabel={option => option.displayLabel}
          getOptionValue={option => option.value}
        />
      </div>
    </div>
  );
};

EditableField.propTypes = {
  value: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  saveData: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onDataChange: PropTypes.func,
  onSaveComplete: PropTypes.func.isRequired,
  changeHandler: PropTypes.func,
  initialData: PropTypes.string,
};

EditableField.defaultProps = {
  onDataChange: null,
  changeHandler: null,
  initialData: '',
};

export default EditableField;
